import { createFileRoute } from '@tanstack/react-router';
import { Building2, User, Sparkles, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { ChangePasswordCard } from '@/features/settings/ChangePasswordCard';
import { MfaCard } from '@/features/settings/MfaCard';
import { EntidadesCard } from '@/features/configuracoes/EntidadesCard';
import { AcordosCard } from '@/features/configuracoes/AcordosCard';
import { CofreCard } from '@/features/configuracoes/CofreCard';
import { AssinaturaCard } from '@/features/configuracoes/AssinaturaCard';
import { useAuth } from '@/lib/auth';

export const Route = createFileRoute('/_app/configuracoes')({
  component: ConfiguracoesPage,
});

function ConfiguracoesPage() {
  const { user } = useAuth();

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <header>
        <Eyebrow>Configurações</Eyebrow>
        <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
          Sua conta
        </h1>
        <p className="text-sm text-fg-muted mt-2">
          Workspace, segurança, IA e integrações com o portal.
        </p>
      </header>

      <Card variant="default" className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
            <User className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <Eyebrow>Perfil</Eyebrow>
            <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
              {user?.name ?? '—'}
            </h3>
            <p className="text-sm text-fg-muted mt-0.5 font-mono">{user?.email ?? '—'}</p>
          </div>
          <Pill tone="mint" dot>
            {roleLabel(user?.role)}
          </Pill>
        </div>
        {user?.workspaceName && (
          <div className="flex items-center gap-2 text-sm text-fg-muted mt-3 pt-3 border-t border-card-stroke">
            <Building2 className="h-4 w-4" strokeWidth={1.8} />
            <span>
              Workspace: <span className="text-fg font-medium">{user.workspaceName}</span>
            </span>
          </div>
        )}
      </Card>

      <ChangePasswordCard />
      <MfaCard />

      {user?.effectiveWorkspaceId && (
        <>
          <AssinaturaCard />
          <EntidadesCard workspaceId={user.effectiveWorkspaceId} />
          <AcordosCard workspaceId={user.effectiveWorkspaceId} />
          <CofreCard workspaceId={user.effectiveWorkspaceId} />
        </>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlaceholderCard
          icon={Sparkles}
          eyebrow="IA"
          title="Modelo e orçamento"
          description="Provider, modelo e limite mensal de gastos com IA."
        />
      </section>
    </div>
  );
}

function PlaceholderCard({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: typeof Building2;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <Card variant="default" className="p-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-2 bg-card-soft text-fg-muted shrink-0">
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
            {title}
          </h3>
          <p className="text-sm text-fg-muted mt-1">{description}</p>
          <Pill tone="lilac" dot className="mt-3">
            em breve
          </Pill>
        </div>
      </div>
    </Card>
  );
}

function roleLabel(role: string | undefined): string {
  switch (role) {
    case 'PLATFORM_OWNER':
      return 'platform owner';
    case 'PLATFORM_SUPPORT':
      return 'platform support';
    case 'WORKSPACE_ADMIN':
      return 'admin';
    case 'OPERADOR':
      return 'operador';
    case 'CONTADOR_EXTERNO':
      return 'contador';
    default:
      return '—';
  }
}
